import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EventLayout from "@/components/event/EventLayout";
import { eventApi } from "@/lib/eventHub";
import { Facebook, Instagram, Linkedin, Globe, ArrowRight, Sparkles } from "lucide-react";

export default function More() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let m = true;
    (async () => { try { const c = await eventApi.config(); if (m) setConfig(c.data); } catch { /* noop */ } })();
    return () => { m = false; };
  }, []);

  const social = config?.social || {};
  const cards = (config?.action_cards || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  const socialLinks = [
    { key: "facebook", url: social.facebook, label: "Business Showers on Facebook", icon: Facebook, testid: "social-facebook" },
    { key: "instagram", url: social.instagram, label: "Business Showers on Instagram", icon: Instagram, testid: "social-instagram" },
    { key: "linkedin_org", url: social.linkedin_org, label: "Business Showers on LinkedIn", icon: Linkedin, testid: "social-linkedin-org" },
    { key: "linkedin_donja", url: social.linkedin_donja, label: "Donja Williams on LinkedIn", icon: Linkedin, testid: "social-linkedin-donja" },
    { key: "donja_website", url: social.donja_website, label: "DonjaWilliams.com", icon: Globe, testid: "social-donja-website" },
  ].filter((s) => s.url);

  const trackClick = (kind, url) => {
    eventApi.track("social_click", { kind, url });
  };

  return (
    <EventLayout showProgramBanner={false}>
      <div className="mx-auto max-w-lg px-5 py-6 space-y-8">
        {/* Stay Connected */}
        <section data-testid="more-social">
          <h1 className="font-[Cormorant_Garamond] text-3xl text-emerald-950 leading-tight">Stay Connected</h1>
          <p className="font-[Outfit] text-sm text-emerald-950/60 mt-1 mb-4">
            Tap a platform, then select Follow.
          </p>
          {socialLinks.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-emerald-950/15 bg-white p-4 text-center text-xs font-[Outfit] text-emerald-950/50">
              Social links coming soon.
            </div>
          ) : (
            <div className="space-y-2">
              {socialLinks.map(({ key, url, label, icon: Icon, testid }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={testid}
                  onClick={() => trackClick(key, url)}
                  className="w-full flex items-center gap-4 rounded-2xl border-2 border-emerald-950/10 bg-white p-4 hover:border-fuchsia-500 transition-colors"
                >
                  <span className="h-11 w-11 rounded-full bg-emerald-950 text-white flex items-center justify-center">
                    <Icon size={20} />
                  </span>
                  <span className="flex-1 font-[Outfit] font-semibold text-emerald-950 text-sm">{label}</span>
                  <ArrowRight size={16} className="text-emerald-950/40" />
                </a>
              ))}
            </div>
          )}
        </section>

        {/* Keep the Momentum */}
        <section data-testid="more-momentum">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-fuchsia-600" />
            <span className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-800">Keep Going</span>
          </div>
          <h2 className="font-[Cormorant_Garamond] text-3xl text-emerald-950 leading-tight">Keep the Momentum Going</h2>
          <p className="font-[Outfit] text-sm text-emerald-950/60 mt-1 mb-4">
            Ways to stay involved after the Business Showers.
          </p>
          {cards.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-emerald-950/15 bg-white p-4 text-center text-xs font-[Outfit] text-emerald-950/50">
              Action cards will appear here soon.
            </div>
          ) : (
            <div className="space-y-3">
              {cards.filter((c) => c.url).map((card) => {
                const isInternal = (card.url || "").startsWith("/");
                const CardTag = isInternal ? Link : "a";
                const cardProps = isInternal
                  ? { to: card.url }
                  : { href: card.url, target: "_blank", rel: "noreferrer" };
                return (
                  <CardTag
                    key={card.id}
                    {...cardProps}
                    data-testid={`momentum-card-${card.id}`}
                    onClick={() => eventApi.track("momentum_click", { id: card.id, url: card.url })}
                    className="block rounded-2xl border-2 border-emerald-950/10 bg-white p-4 transition-all hover:border-fuchsia-500"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-[Cormorant_Garamond] text-lg leading-tight text-emerald-950">{card.title}</div>
                        <div className="text-xs font-[Outfit] text-emerald-950/60 mt-1 leading-relaxed">{card.description}</div>
                      </div>
                      <ArrowRight size={16} className="text-fuchsia-600 shrink-0 mt-1" />
                    </div>
                    <div className="mt-3 text-[11px] font-[Outfit] font-bold uppercase tracking-wider text-fuchsia-700">
                      {card.cta || "Learn more"} →
                    </div>
                  </CardTag>
                );
              })}
            </div>
          )}
        </section>

        {/* Early access CTA — always visible */}
        <section
          data-testid="more-early-access-cta"
          className="rounded-2xl border-2 border-fuchsia-600 bg-fuchsia-600 text-white p-5"
        >
          <div className="text-[10px] tracking-[0.2em] uppercase font-[Outfit] font-bold text-fuchsia-100 mb-1">2027 Early Access</div>
          <div className="font-[Cormorant_Garamond] text-2xl leading-tight">Be first for next year</div>
          <div className="text-sm font-[Outfit] text-fuchsia-100 mt-1 mb-3">Tickets, honoree applications, sponsor slots — all before public launch.</div>
          <Link
            to="/event/early-access"
            data-testid="more-early-access-btn"
            className="inline-flex items-center gap-1 rounded-full bg-white text-fuchsia-700 font-[Outfit] font-bold text-sm px-4 py-2 hover:bg-fuchsia-50 transition-colors"
          >
            Join the 2027 list <ArrowRight size={14} />
          </Link>
        </section>
      </div>
    </EventLayout>
  );
}
